import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as taskListTemplateItemActions from '@redux/modules/taskListTemplateItem'

import defaultFormState, { requiredFields } from '@models/taskListTemplateItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { TaskListTemplateItemModel, TaskListTemplateItemRequestOptions } from '@models/types'

export const generateUrls = (taskListTemplateItem?: Partial<TaskListTemplateItemModel>) => {
  const { id } = taskListTemplateItem || {}

  return {
    editTaskListTemplateItemDetailsUrl: `#/sectionName/taskListTemplateItems/${id}/edit/details`,
    editTaskListTemplateItemUrl: `#/sectionName/taskListTemplateItems/${id}/edit/`,
    taskListTemplateItemsIndexUrl: '#/sectionName/taskListTemplateItems',
  }
}

type CreateTaskListTemplateItemParams = {
  dispatch: AppDispatch,
  requestOptions?: TaskListTemplateItemRequestOptions,
  taskListTemplateItemParams: Partial<TaskListTemplateItemModel>,
}

const createTaskListTemplateItem = (params: CreateTaskListTemplateItemParams) => {
  const { dispatch, taskListTemplateItemParams, requestOptions } = params
  const { createTaskListTemplateItem: createFn } = taskListTemplateItemActions

  return dispatch(createFn(taskListTemplateItemParams, requestOptions))
}

type UpdateTaskListTemplateItemParams = {
  dispatch: AppDispatch,
  requestOptions?: TaskListTemplateItemRequestOptions,
  taskListTemplateItem: TaskListTemplateItemModel,
  taskListTemplateItemParams: Partial<TaskListTemplateItemModel>,
}

const updateTaskListTemplateItem = (params: UpdateTaskListTemplateItemParams) => {
  const {
    dispatch, taskListTemplateItem, taskListTemplateItemParams, requestOptions,
  } = params
  const { updateTaskListTemplateItem: updateFn } = taskListTemplateItemActions

  const updatedParams = {
    id: taskListTemplateItem.id,
    ...taskListTemplateItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTaskListTemplateItemParams = {
  dispatch: AppDispatch,
  taskListTemplateItem: DeleteParams<TaskListTemplateItemModel>,
}

const deleteTaskListTemplateItem = (params: DeleteTaskListTemplateItemParams) => {
  const { dispatch, taskListTemplateItem } = params
  const { deleteTaskListTemplateItem: deleteFn } = taskListTemplateItemActions

  return dispatch(deleteFn(taskListTemplateItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTaskListTemplateItemForm(
  taskListTemplateItem: Partial<TaskListTemplateItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const taskListTemplateItemForm = useForm(
    defaultFormState,
    { entity: taskListTemplateItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [taskListTemplateItem.id, taskListTemplateItem.cache_key],
  )

  return {
    ...taskListTemplateItemForm,
  }
}

export const useRelations = (taskListTemplateItem: Partial<TaskListTemplateItemModel> = {}) => {
  const { task_list_template_id, task_list_template_section_id } = taskListTemplateItem

  const { taskListTemplates, taskListTemplateSections } = useSelector(reduxState => reduxState.entities)

  const taskListTemplate = task_list_template_id && taskListTemplates[task_list_template_id]
    ? taskListTemplates[task_list_template_id]
    : {}

  const taskListTemplateSection = task_list_template_section_id
    && taskListTemplateSections[task_list_template_section_id]
    ? taskListTemplateSections[task_list_template_section_id]
    : {}

  return {
    taskListTemplate,
    taskListTemplateSection,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: TaskListTemplateItemRequestOptions,
}

function useTaskListTemplateItem(initEntity: Partial<TaskListTemplateItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  const { entity: taskListTemplateItem }: { entity: TaskListTemplateItemModel} = useLatestEntity(initEntity, 'taskListTemplateItems')
  const { id } = taskListTemplateItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { taskListTemplate, taskListTemplateSection } = useRelations(taskListTemplateItem)

  useReduxAction(
    'taskListTemplateItems',
    'loadTaskListTemplateItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.taskListTemplateItems)

  return {
    callbacks: {
      createTaskListTemplateItem: (
        taskListTemplateItemParams: Partial<TaskListTemplateItemModel>,
        entityOptions?: TaskListTemplateItemRequestOptions,
      ) => (
        createTaskListTemplateItem({ taskListTemplateItemParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditTaskListTemplateItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTaskListTemplateItemModal',
        payload: { taskListTemplateItem },
      }),
      deleteTaskListTemplateItem: () => deleteTaskListTemplateItem({ dispatch, taskListTemplateItem }),
      updateTaskListTemplateItem: (
        taskListTemplateItemParams: Partial<TaskListTemplateItemModel>,
        entityOptions?: TaskListTemplateItemRequestOptions,
      ) => (
        updateTaskListTemplateItem({
          taskListTemplateItem, taskListTemplateItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    taskListTemplate,
    taskListTemplateItem,
    taskListTemplateSection,
    updating,
    urls: generateUrls(taskListTemplateItem),
  }
}

export default useTaskListTemplateItem
